import React, { useRef, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';
import type { Customer } from '../../types/customer';
import { booleanBodyTemplate, listBodyTemplate, createActionBodyTemplate, countryBodyTemplate, languageBodyTemplate } from './columnTemplates';
import { CellEditor } from './CellEditor';

interface CustomerTableProps {
  customers: Customer[];
  selectedCustomers: Customer[] | null;
  onSelectionChange: (customers: Customer[]) => void;
  onEdit: (customer: Customer) => void;
  onEditForm: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  globalFilter: string | null;
  onGlobalFilterChange: (value: string) => void;
  currentOrganization: string;
  isAdmin: boolean;
  dtRef?: React.RefObject<any>;
  // New props for action buttons
  onNew: () => void;
  onDeleteSelected: () => void;
  onImportCSV: (e: any) => void;
  onExportCSV: () => void;
  onHelp: () => void;
  onViewChangelog?: () => void;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  selectedCustomers,
  onSelectionChange,
  onEdit,
  onEditForm,
  onDelete,
  globalFilter,
  onGlobalFilterChange,
  currentOrganization,
  isAdmin,
  dtRef,
  onNew,
  onDeleteSelected,
  onImportCSV,
  onExportCSV,
  onHelp,
  onViewChangelog,
}) => {
  // State declarations MUST come before useEffect
  const internalDtRef = useRef<any>(null);
  const dt = dtRef || internalDtRef;
  const fileRef = useRef<any>(null);
  const [showOrgFilter, setShowOrgFilter] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const orgFilterRef = useRef<HTMLDivElement>(null);
  const filterIconRef = useRef<HTMLElement>(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showCountryFilter, setShowCountryFilter] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const countryFilterRef = useRef<HTMLDivElement>(null);
  const [countryFilterPosition, setCountryFilterPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // Column order state - persisted in localStorage
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem('customerTableColumnOrder');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Selection state
  const [selectAllMode, setSelectAllMode] = useState<'page' | 'all'>('page');
  const [showSelectAllBanner, setShowSelectAllBanner] = useState(false);
  const [currentPageData, setCurrentPageData] = useState<Customer[]>([]);
  const [pageFirst, setPageFirst] = useState(0);
  const [pageRows, setPageRows] = useState(10);
  
  // Cell editor state
  const [editingCell, setEditingCell] = useState<{
    customer: Customer;
    field: keyof Customer;
    value: any;
    cellRect: DOMRect;
  } | null>(null);
  
  // Inline dropdown state for country/language
  const [inlineDropdown, setInlineDropdown] = useState<{
    customer: Customer;
    field: 'country' | 'language';
    cellElement: HTMLElement;
  } | null>(null);
  const dropdownRef = useRef<any>(null);
  
  // Inline checkbox state for PKW/NKW
  const [inlineCheckbox, setInlineCheckbox] = useState<{
    customer: Customer;
    field: 'pkwData' | 'lkwData';
  } | null>(null);
  
  // Auto-open dropdown when inline editing starts
  useEffect(() => {
    if (inlineDropdown && dropdownRef.current) {
      // Wait for dropdown to render, then open it
      setTimeout(() => {
        if (dropdownRef.current) {
          dropdownRef.current.show();
        }
      }, 50);
    }
  }, [inlineDropdown]);
  
  // Save column order to localStorage when it changes
  const handleColumnReorder = (e: any) => {
    const newOrder = e.columns.map((col: any) => col.props.field).filter(Boolean);
    setColumnOrder(newOrder);
    localStorage.setItem('customerTableColumnOrder', JSON.stringify(newOrder));
  };
  
  // Close organization filter when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOrgFilter && orgFilterRef.current && !orgFilterRef.current.contains(event.target as Node)) {
        setShowOrgFilter(false);
      }
      if (showCountryFilter && countryFilterRef.current && !countryFilterRef.current.contains(event.target as Node)) {
        setShowCountryFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOrgFilter, showCountryFilter]);

  // Update filter position when it opens
  useEffect(() => {
    if (showOrgFilter) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        if (filterIconRef.current) {
          const rect = filterIconRef.current.getBoundingClientRect();
          setFilterPosition({
            top: rect.bottom + 8,
            left: rect.left
          });
        }
      }, 0);
    }
  }, [showOrgFilter]);

  // Also update position on scroll/resize
  useEffect(() => {
    if (!showOrgFilter) return;

    const updatePosition = () => {
      if (filterIconRef.current) {
        const rect = filterIconRef.current.getBoundingClientRect();
        setFilterPosition({
          top: rect.bottom + 8,
          left: rect.left
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showOrgFilter]);

  // Get unique organizations from customers
  const uniqueOrganizations = useMemo(() => {
    const orgs = new Set(customers.map(c => c.organization).filter(Boolean));
    return Array.from(orgs).sort();
  }, [customers]);

  // Get unique countries from customers
  const uniqueCountries = useMemo(() => {
    const countries = new Set(customers.map(c => c.country).filter(Boolean));
    return Array.from(countries).sort();
  }, [customers]);

  // Filter customers based on selected organizations and countries
  const filteredCustomers = useMemo(() => {
    let filtered = customers;
    
    // Apply organization filter
    if (selectedOrgs.length > 0) {
      filtered = filtered.filter(c => selectedOrgs.includes(c.organization));
    }
    
    // Apply country filter
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(c => selectedCountries.includes(c.country));
    }
    
    return filtered;
  }, [customers, selectedOrgs, selectedCountries]);

  // Detect when all visible rows on the current page are selected
  useEffect(() => {
    if (!selectedCustomers || selectedCustomers.length === 0) {
      setShowSelectAllBanner(false);
      setSelectAllMode('page');
      return;
    }

    // How many rows are on the current page?
    const remainingRows = filteredCustomers.length - pageFirst;
    const currentPageSize = Math.min(pageRows, remainingRows);

    // If selected count matches the page size, the header checkbox was used
    const allPageSelected = selectedCustomers.length >= currentPageSize && currentPageSize > 0;

    // Show banner only when all page rows selected but not all customers total
    const notAllCustomersSelected = selectedCustomers.length < filteredCustomers.length;
    setShowSelectAllBanner(allPageSelected && notAllCustomersSelected);

    // Reset to page mode if selection shrinks below total
    if (selectAllMode === 'all' && selectedCustomers.length < filteredCustomers.length) {
      setSelectAllMode('page');
    }
  }, [selectedCustomers, filteredCustomers, selectAllMode, pageFirst, pageRows]);

  // Country code to English name mapping
  const countryNames: { [key: string]: string } = {
    'FR': 'France',
    'DE': 'Germany',
    'IT': 'Italy',
    'ES': 'Spain',
    'GB': 'United Kingdom',
    'UK': 'United Kingdom',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'AT': 'Austria',
    'CH': 'Switzerland',
    'PL': 'Poland',
    'PT': 'Portugal',
    'SE': 'Sweden',
    'DK': 'Denmark',
    'NO': 'Norway',
    'FI': 'Finland',
    'IE': 'Ireland',
    'CZ': 'Czech Republic',
    'HU': 'Hungary',
    'RO': 'Romania',
    'GR': 'Greece',
    'BG': 'Bulgaria',
    'HR': 'Croatia',
    'SK': 'Slovakia',
    'SI': 'Slovenia',
    'LT': 'Lithuania',
    'LV': 'Latvia',
    'EE': 'Estonia',
    'LU': 'Luxembourg',
    'MT': 'Malta',
    'CY': 'Cyprus'
  };

  // Custom organization header with filter
  const organizationHeader = () => {
    const handleHeaderClick = (e: React.MouseEvent) => {
      // Don't open filter if clicking on sort icons
      const target = e.target as HTMLElement;
      if (target.classList.contains('p-sortable-column-icon') || 
          target.closest('.p-sortable-column-icon')) {
        return;
      }
      
      e.stopPropagation();
      // Find the parent th element (column header cell)
      let thElement = target.closest('th');
      if (thElement) {
        const rect = thElement.getBoundingClientRect();
        setFilterPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
      setShowOrgFilter(!showOrgFilter);
    };

    return (
      <div 
        style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}
        onClick={handleHeaderClick}
      >
        <span>Organization</span>
        <i 
          className="pi pi-filter" 
          style={{ fontSize: '0.8rem', color: selectedOrgs.length > 0 ? '#4A90E2' : '#666' }}
        />
      </div>
    );
  };

  // Custom country header with filter
  const countryHeader = () => {
    const handleHeaderClick = (e: React.MouseEvent) => {
      // Don't open filter if clicking on sort icons
      const target = e.target as HTMLElement;
      if (target.classList.contains('p-sortable-column-icon') || 
          target.closest('.p-sortable-column-icon')) {
        return;
      }
      
      e.stopPropagation();
      // Find the parent th element (column header cell)
      let thElement = target.closest('th');
      if (thElement) {
        const rect = thElement.getBoundingClientRect();
        setCountryFilterPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
      setShowCountryFilter(!showCountryFilter);
    };

    return (
      <div 
        style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}
        onClick={handleHeaderClick}
      >
        <span>Country</span>
        <i 
          className="pi pi-filter" 
          style={{ fontSize: '0.8rem', color: selectedCountries.length > 0 ? '#4A90E2' : '#666' }}
        />
      </div>
    );
  };

  // Render filter dropdown using portal (outside table DOM)
  const renderFilterDropdown = () => {
    if (!showOrgFilter) return null;

    return createPortal(
      <div 
        ref={orgFilterRef}
        style={{
          position: 'fixed',
          top: `${filterPosition.top}px`,
          left: `${filterPosition.left}px`,
          width: `${filterPosition.width}px`,
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 9999,
          maxHeight: '300px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>
          <strong style={{ fontSize: '0.875rem' }}>Filter by Organization</strong>
        </div>
        <div style={{ padding: '0.25rem' }}>
          {uniqueOrganizations.map(org => (
            <div 
              key={org}
              style={{ 
                padding: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => {
                setSelectedOrgs(prev => 
                  prev.includes(org) 
                    ? prev.filter(o => o !== org)
                    : [...prev, org]
                );
              }}
            >
              <i className={selectedOrgs.includes(org) ? 'pi pi-check-square' : 'pi pi-square'} />
              <span>{org}</span>
            </div>
          ))}
        </div>
        <div style={{ padding: '0.5rem', borderTop: '1px solid #eee', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <i 
            className="pi pi-times" 
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#666' }}
            title="Clear selection"
            onClick={() => setSelectedOrgs([])}
          />
          <i 
            className="pi pi-check" 
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#4A90E2' }}
            title="Close"
            onClick={() => setShowOrgFilter(false)}
          />
        </div>
      </div>,
      document.body
    );
  };

  // Render country filter dropdown using portal
  const renderCountryFilterDropdown = () => {
    if (!showCountryFilter) return null;

    return createPortal(
      <div 
        ref={countryFilterRef}
        style={{
          position: 'fixed',
          top: `${countryFilterPosition.top}px`,
          left: `${countryFilterPosition.left}px`,
          width: `${countryFilterPosition.width}px`,
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 9999,
          maxHeight: '300px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>
          <strong style={{ fontSize: '0.875rem' }}>Filter by Country</strong>
        </div>
        <div style={{ padding: '0.25rem' }}>
          {uniqueCountries.map(country => (
            <div 
              key={country}
              style={{ 
                padding: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => {
                setSelectedCountries(prev => 
                  prev.includes(country) 
                    ? prev.filter(c => c !== country)
                    : [...prev, country]
                );
              }}
            >
              <i className={selectedCountries.includes(country) ? 'pi pi-check-square' : 'pi pi-square'} />
              <img 
                src={`https://flagcdn.com/24x18/${country.toLowerCase()}.png`}
                alt={country}
                style={{ 
                  width: '24px', 
                  height: '18px', 
                  borderRadius: '2px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span>{countryNames[country] || country}</span>
            </div>
          ))}
        </div>
        <div style={{ padding: '0.5rem', borderTop: '1px solid #eee', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <i 
            className="pi pi-times" 
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#666' }}
            title="Clear selection"
            onClick={() => setSelectedCountries([])}
          />
          <i 
            className="pi pi-check" 
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#4A90E2' }}
            title="Close"
            onClick={() => setShowCountryFilter(false)}
          />
        </div>
      </div>,
      document.body
    );
  };

  // Create action template with callbacks
  const actionBodyTemplate = useMemo(
    () => createActionBodyTemplate(onEdit, onDelete),
    [onEdit, onDelete]
  );

  // Custom email body template - stacks primary and secondary emails
  const emailBodyTemplate = (rowData: Customer) => {
    // Handle case where primaryEmail might be an object (debugging data)
    let primaryEmail = rowData.primaryEmail;
    if (typeof primaryEmail === 'object') {
      // If it's an object, try to extract the actual email or show empty
      primaryEmail = '';
    }
    
    // secondaryEmail is now a string, not an array
    // Filter out empty strings, "[]", and whitespace
    const secondaryEmail = rowData.secondaryEmail || "";
    const hasSecondaryEmail = secondaryEmail && 
                              secondaryEmail.trim() !== '' && 
                              secondaryEmail !== '[]';
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
        <div style={{ fontWeight: '500' }}>{primaryEmail || '(empty)'}</div>
        {hasSecondaryEmail && (
          <div style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
            {secondaryEmail}
          </div>
        )}
      </div>
    );
  };

  // Custom company body template - stacks primary and secondary companies
  const companyBodyTemplate = (rowData: Customer) => {
    const company2List = rowData.company2 || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
        <div style={{ fontWeight: '500' }}>{rowData.company}</div>
        {company2List.length > 0 && company2List.map((company, index) => (
          <div key={index} style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
            {company}
          </div>
        ))}
      </div>
    );
  };

  const validationBodyTemplate = (rowData: Customer, column: { field: string }) => {
    const isEnabled = rowData[column.field as keyof Customer];
    return (
      <React.Fragment>
        <i className={isEnabled ? "pi pi-check" : "pi pi-times"}></i>
      </React.Fragment>
    );
  };

  const rowClassName = (row: Customer) => {
    // The backend is the single authority for validity: it stamps
    // validationState ('valid' | 'invalid') on every create/update using the
    // canonical business rules. The table simply reflects that — no separate
    // (and previously divergent) client-side rule set here.
    return row.validationState === 'invalid' ? "invalid-row-color" : "";
  };

  // Handle cell click for inline editing
  const handleCellClick = (e: any, customer: Customer, field: keyof Customer) => {
    // Don't edit on checkbox column or non-editable fields
    const nonEditableFields = ['customerId', 'createdAt', 'updatedAt', 'updatedBy', 'owner', 'valid'];
    if (nonEditableFields.includes(field)) return;

    // For boolean fields (PKW/NKW), show inline checkbox
    if (field === 'pkwData' || field === 'lkwData') {
      setInlineCheckbox({ customer, field });
      return;
    }

    // For country and language, we'll handle them with inline dropdowns
    // (handled in the cell template itself)
    if (field === 'country' || field === 'language') {
      // These will be handled by inline dropdowns in the cell templates
      return;
    }

    const target = e.target as HTMLElement;
    const cell = target.closest('td');
    if (!cell) return;

    const cellRect = cell.getBoundingClientRect();
    setEditingCell({
      customer,
      field,
      value: customer[field],
      cellRect,
    });
  };

  // Handle save from cell editor
  const handleCellSave = (value: any) => {
    if (!editingCell) return;

    let updatedCustomer;
    
    // Check if value includes validation state (new format)
    const hasValidationState = typeof value === 'object' && 'validationState' in value;
    
    // Special handling for email field (returns object with primary and secondary)
    if (editingCell.field === 'primaryEmail' && typeof value === 'object' && value.primaryEmail) {
      updatedCustomer = {
        ...editingCell.customer,
        primaryEmail: value.primaryEmail,
        secondaryEmail: value.secondaryEmail,
        validationState: value.validationState,
      };
    } else if (editingCell.field === 'company' && typeof value === 'object' && value.company !== undefined) {
      // Special handling for company field (returns object with company and company2)
      updatedCustomer = {
        ...editingCell.customer,
        company: value.company,
        company2: value.company2,
        validationState: value.validationState,
      };
    } else if (hasValidationState) {
      // New format with validation state
      updatedCustomer = {
        ...editingCell.customer,
        [editingCell.field]: value.value !== undefined ? value.value : value,
        validationState: value.validationState,
      };
    } else {
      // Legacy format without validation state (backward compatibility)
      updatedCustomer = {
        ...editingCell.customer,
        [editingCell.field]: value,
      };
    }

    // CRITICAL: Close editor SYNCHRONOUSLY using setTimeout to ensure DOM update
    // This prevents the editor from blocking other UI interactions
    setEditingCell(null);
    
    // Use setTimeout to ensure the state update completes before calling onEdit
    // This gives React time to unmount the CellEditor component
    setTimeout(() => {
      onEdit(updatedCustomer);
    }, 0);
  };

  // Handle cancel from cell editor
  const handleCellCancel = () => {
    setEditingCell(null);
  };

  // Handle "Select All" across all pages
  const handleSelectAll = () => {
    onSelectionChange(filteredCustomers);
    setSelectAllMode('all');
    setShowSelectAllBanner(false);
  };

  // Handle "Clear Selection"
  const handleClearSelection = () => {
    onSelectionChange([]);
    setSelectAllMode('page');
    setShowSelectAllBanner(false);
  };

  // Create editable cell body templates
  const createEditableCellTemplate = (field: keyof Customer) => {
    return (rowData: Customer) => {
      const value = rowData[field];
      
      // Always validate the actual value
      let isInvalid = false;
      let errorMessage = '';
      
      if (field === 'customerNr') {
        if (!value || value.toString().trim() === '') {
          isInvalid = true;
          errorMessage = 'Customer number is required';
        }
      }
      
      return (
        <div 
          onClick={(e) => handleCellClick(e, rowData, field)}
          className={isInvalid ? 'invalid-cell' : ''}
          title={errorMessage}
          style={{ cursor: 'pointer', minHeight: '1.5rem', textAlign: 'left', width: '100%' }}
        >
          {value || ''}
        </div>
      );
    };
  };

  // Editable email template
  const editableEmailTemplate = (rowData: Customer) => {
    // Handle case where primaryEmail might be an object (debugging data)
    let primaryEmail = rowData.primaryEmail;
    if (typeof primaryEmail === 'object') {
      // If it's an object, try to extract the actual email or show empty
      primaryEmail = '';
    }
    
    // secondaryEmail is now a string, not an array
    // Filter out empty strings, "[]", and whitespace
    const secondaryEmail = rowData.secondaryEmail || "";
    const hasSecondaryEmail = secondaryEmail && 
                              secondaryEmail.trim() !== '' && 
                              secondaryEmail !== '[]';
    
    // Always validate email format
    let isInvalid = false;
    let errorMessage = '';
    const emailRegex = /^[^\s@,]+@[^\s@,]+\.[A-Za-z]{2,}$/;
    if (!primaryEmail || !emailRegex.test(primaryEmail)) {
      isInvalid = true;
      errorMessage = !primaryEmail ? 'Email is required' : `Invalid email format: ${primaryEmail}`;
    }
    
    return (
      <div 
        onClick={(e) => handleCellClick(e, rowData, 'primaryEmail')}
        className={isInvalid ? 'invalid-cell' : ''}
        title={errorMessage}
        style={{ cursor: 'pointer', minHeight: '1.5rem' }}
      >
        <div style={{ fontWeight: 'normal' }}>{primaryEmail || '(empty)'}</div>
        {hasSecondaryEmail && (
          <div style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic', marginTop: '0.15rem' }}>
            {secondaryEmail}
          </div>
        )}
      </div>
    );
  };

  // Editable company template
  const editableCompanyTemplate = (rowData: Customer) => {
    const company = rowData.company;
    // Handle company2 as either string or array
    const company2Raw = rowData.company2 || '';
    const company2 = typeof company2Raw === 'string' 
      ? company2Raw.split(',').map(c => c.trim()).filter(c => c !== '' && c !== '[]')
      : Array.isArray(company2Raw) 
        ? company2Raw.filter(c => c !== '' && c !== '[]')
        : [];
    
    // Always validate company
    let isInvalid = false;
    let errorMessage = '';
    if (!company || company.trim() === '') {
      isInvalid = true;
      errorMessage = 'Company is required';
    }
    
    return (
      <div 
        onClick={(e) => handleCellClick(e, rowData, 'company')}
        className={isInvalid ? 'invalid-cell' : ''}
        title={errorMessage}
        style={{ cursor: 'pointer', minHeight: '1.5rem' }}
      >
        <div style={{ fontWeight: 'normal' }}>{company}</div>
        {company2.length > 0 && (
          <div style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic', marginTop: '0.15rem' }}>
            {company2.join(', ')}
          </div>
        )}
      </div>
    );
  };

  // Editable phone template
  const editablePhoneTemplate = (rowData: Customer) => {
    // Handle both string and array formats
    const phoneValue = rowData.telephone;
    const phones = Array.isArray(phoneValue) ? phoneValue : (phoneValue ? [phoneValue] : []);
    
    // Always validate phone format
    const e164Regex = /^\+[0-9]{5,15}$/;
    const invalidPhones = phones.filter(phone => phone && phone.trim() !== '' && !e164Regex.test(phone));
    const isInvalid = phones.length === 0 || invalidPhones.length > 0;
    const errorMessage = phones.length === 0 
      ? 'Phone number is required' 
      : invalidPhones.length > 0 
        ? `Invalid phone format: ${invalidPhones.join(', ')}` 
        : '';
    
    return (
      <div 
        onClick={(e) => handleCellClick(e, rowData, 'telephone')}
        className={isInvalid ? 'invalid-cell' : ''}
        title={errorMessage}
        style={{ cursor: 'pointer', minHeight: '1.5rem', textAlign: 'left', width: '100%' }}
      >
        {phones.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', alignItems: 'flex-start', textAlign: 'left' }}>
            {phones.map((phone, index) => {
              const phoneInvalid = phone && phone.trim() !== '' && !e164Regex.test(phone);
              return (
                <div 
                  key={index} 
                  style={{ 
                    textAlign: 'left',
                    color: phoneInvalid ? '#c63737' : 'inherit',
                    fontWeight: phoneInvalid ? 'bold' : 'normal'
                  }}
                  title={phoneInvalid ? `Invalid phone format: ${phone} (expected: +[digits], 5-15 digits)` : ''}
                >
                  {phone}
                </div>
              );
            })}
          </div>
        ) : ''}
      </div>
    );
  };

  // Editable country template (with inline dropdown)
  const editableCountryTemplate = (rowData: Customer) => {
    const countryCode = rowData.country?.toUpperCase();
    const countryName = countryNames[countryCode] || countryCode;
    
    // Always validate country
    let isInvalid = false;
    let errorMessage = '';
    if (!countryCode || countryCode.trim() === '') {
      isInvalid = true;
      errorMessage = 'Country is required';
    }
    
    const isEditing = inlineDropdown?.customer.customerId === rowData.customerId && inlineDropdown?.field === 'country';
    
    if (isEditing) {
      const countries = [
        { code: 'FR', name: 'France' },
        { code: 'DE', name: 'Germany' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'IT', name: 'Italy' },
        { code: 'ES', name: 'Spain' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'BE', name: 'Belgium' },
        { code: 'AT', name: 'Austria' },
        { code: 'CH', name: 'Switzerland' },
        { code: 'PL', name: 'Poland' },
        { code: 'PT', name: 'Portugal' },
        { code: 'SE', name: 'Sweden' },
        { code: 'DK', name: 'Denmark' },
        { code: 'NO', name: 'Norway' },
        { code: 'FI', name: 'Finland' },
      ];
      
      const countryOptionTemplate = (option: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img 
            src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
            alt={`${option.code} flag`}
            style={{ borderRadius: '2px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
          />
          <span>{option.name}</span>
        </div>
      );
      
      return (
        <Dropdown
          ref={dropdownRef}
          value={countryCode}
          options={countries}
          onChange={(e) => {
            const updatedCustomer = {
              ...rowData,
              country: e.value,
              validationState: 'valid' as const,
            };
            setInlineDropdown(null);
            onEdit(updatedCustomer);
          }}
          optionLabel="name"
          optionValue="code"
          itemTemplate={countryOptionTemplate}
          placeholder="Select country"
          style={{ width: '100%', fontSize: '0.875rem' }}
          panelStyle={{ zIndex: 10001, minWidth: '200px' }}
          appendTo={document.body}
          onHide={() => setInlineDropdown(null)}
        />
      );
    }
    
    return (
      <div 
        onClick={(e) => {
          const cell = (e.target as HTMLElement).closest('td');
          if (cell) {
            setInlineDropdown({ customer: rowData, field: 'country', cellElement: cell as HTMLElement });
          }
        }}
        className={isInvalid ? 'invalid-cell' : ''}
        title={errorMessage}
        style={{ cursor: 'pointer', minHeight: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', width: '100%' }}
      >
        {countryCode && (
          <img 
            src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
            alt={`${countryCode} flag`}
            title={countryName}
            style={{ 
              borderRadius: '2px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              display: 'block',
              margin: '0 auto'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling!.textContent = countryCode;
            }}
          />
        )}
        <span style={{ display: 'none' }}></span>
      </div>
    );
  };

  // Editable language template (with inline dropdown)
  const editableLanguageTemplate = (rowData: Customer) => {
    const langCode = rowData.language?.toLowerCase();
    const languageNames: { [key: string]: string } = {
      'en': 'English', 'de': 'German', 'fr': 'French', 'es': 'Spanish',
      'it': 'Italian', 'nl': 'Dutch', 'pt': 'Portuguese', 'pl': 'Polish',
      'sv': 'Swedish', 'da': 'Danish', 'no': 'Norwegian', 'fi': 'Finnish',
      'cs': 'Czech', 'hu': 'Hungarian', 'ro': 'Romanian', 'bg': 'Bulgarian',
      'el': 'Greek', 'tr': 'Turkish', 'ru': 'Russian', 'uk': 'Ukrainian',
      'ar': 'Arabic', 'zh': 'Chinese', 'ja': 'Japanese', 'ko': 'Korean'
    };
    const languageColors: { [key: string]: string } = {
      'en': '#4A90E2', 'de': '#E24A4A', 'fr': '#5BC0DE', 'es': '#F0AD4E',
      'it': '#5CB85C', 'nl': '#FF8C00', 'pt': '#9B59B6', 'pl': '#E74C3C',
      'sv': '#3498DB', 'da': '#E67E22', 'no': '#1ABC9C', 'fi': '#34495E',
      'cs': '#95A5A6', 'hu': '#16A085', 'ro': '#C0392B', 'bg': '#8E44AD'
    };
    const bgColor = languageColors[langCode] || '#6c757d';
    const langName = languageNames[langCode] || langCode?.toUpperCase();
    
    // Always validate language
    let isInvalid = false;
    let errorMessage = '';
    if (!langCode || langCode.trim() === '') {
      isInvalid = true;
      errorMessage = 'Language is required';
    }
    
    const isEditing = inlineDropdown?.customer.customerId === rowData.customerId && inlineDropdown?.field === 'language';
    
    if (isEditing) {
      const languages = [
        { code: 'en', name: 'English' },
        { code: 'de', name: 'German' },
        { code: 'fr', name: 'French' },
        { code: 'es', name: 'Spanish' },
        { code: 'it', name: 'Italian' },
        { code: 'nl', name: 'Dutch' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'pl', name: 'Polish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'da', name: 'Danish' },
        { code: 'no', name: 'Norwegian' },
        { code: 'fi', name: 'Finnish' },
      ];
      
      const languageOptionTemplate = (option: any) => {
        const color = languageColors[option.code] || '#6c757d';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '0.15rem 0.4rem',
                backgroundColor: color,
                color: 'white',
                borderRadius: '3px',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                minWidth: '2rem',
                textAlign: 'center'
              }}
            >
              {option.code}
            </span>
            <span>{option.name}</span>
          </div>
        );
      };
      
      return (
        <Dropdown
          ref={dropdownRef}
          value={langCode}
          options={languages}
          onChange={(e) => {
            const updatedCustomer = {
              ...rowData,
              language: e.value,
              validationState: 'valid' as const,
            };
            setInlineDropdown(null);
            onEdit(updatedCustomer);
          }}
          optionLabel="name"
          optionValue="code"
          itemTemplate={languageOptionTemplate}
          placeholder="Select language"
          style={{ width: '100%', fontSize: '0.875rem' }}
          panelStyle={{ zIndex: 10001, minWidth: '200px' }}
          appendTo={document.body}
          onHide={() => setInlineDropdown(null)}
        />
      );
    }
    
    return (
      <div 
        onClick={(e) => {
          const cell = (e.target as HTMLElement).closest('td');
          if (cell) {
            setInlineDropdown({ customer: rowData, field: 'language', cellElement: cell as HTMLElement });
          }
        }}
        className={isInvalid ? 'invalid-cell' : ''}
        title={errorMessage}
        style={{ cursor: 'pointer', minHeight: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
      >
        {langCode && (
          <span
            title={langName}
            style={{
              display: 'inline-block',
              padding: '0.15rem 0.4rem',
              backgroundColor: bgColor,
              color: 'white',
              borderRadius: '3px',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {langCode}
          </span>
        )}
      </div>
    );
  };

  // Editable boolean template (PKW/NKW)
  const editableBooleanTemplate = (field: 'pkwData' | 'lkwData') => {
    return (rowData: Customer) => {
      const isEnabled = rowData[field];
      const isEditing = inlineCheckbox?.customer.customerId === rowData.customerId && inlineCheckbox?.field === field;
      
      if (isEditing) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '1.5rem' }}>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                const updatedCustomer = {
                  ...rowData,
                  [field]: newValue,
                  validationState: 'valid' as const,
                };
                setInlineCheckbox(null);
                onEdit(updatedCustomer);
              }}
              style={{ 
                cursor: 'pointer',
                width: '1.2rem',
                height: '1.2rem',
                accentColor: '#4A90E2'
              }}
              autoFocus
            />
          </div>
        );
      }
      
      return (
        <div 
          onClick={(e) => handleCellClick(e, rowData, field)}
          style={{ cursor: 'pointer', minHeight: '1.5rem', textAlign: 'center' }}
        >
          <i className={isEnabled ? "pi pi-check" : "pi pi-times"}></i>
        </div>
      );
    };
  };

  // Edit button template
  const editButtonTemplate = (rowData: Customer) => {
    return (
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text p-button-sm"
        onClick={(e) => {
          e.stopPropagation();
          onEditForm(rowData);
        }}
        tooltip="Edit customer"
        tooltipOptions={{ position: 'left' }}
        style={{ width: '2rem', height: '2rem' }}
      />
    );
  };

  const header = (
    <div className="table-header" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
      {/* Left side: Action buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', flex: '0 0 auto' }}>
        <Button 
          icon="pi pi-plus" 
          className="p-button-success p-button-sm" 
          onClick={onNew}
          disabled={selectedCustomers && selectedCustomers.length > 0}
          tooltip="Add new customer"
          tooltipOptions={{ position: 'bottom' }}
        />
        <Button 
          icon="pi pi-trash" 
          className="p-button-danger p-button-sm" 
          onClick={onDeleteSelected} 
          tooltip="Delete selected entries"
          tooltipOptions={{ position: 'bottom' }}
          style={{ 
            visibility: selectedCustomers && selectedCustomers.length > 0 ? 'visible' : 'hidden'
          }}
        />
      </div>
      
      {/* Center: Title */}
      <h5 className="m-0" style={{ flex: '1 1 auto', textAlign: 'center' }}>Customers of {currentOrganization}</h5>
      
      {/* Right side: Search + Upload/Download/Help buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', flex: '0 0 auto', alignItems: 'center' }}>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText 
            type="search" 
            size="small"
            onInput={(e) => onGlobalFilterChange((e.target as HTMLInputElement).value)} 
            placeholder="Search..." 
          />
        </span>
        <Button 
          icon="pi pi-upload" 
          className="p-button-sm" 
          onClick={() => fileRef.current?.getInput()?.click()}
          tooltip="Upload CSV file"
          tooltipOptions={{ position: 'bottom' }}
        />
        <FileUpload 
          name="data" 
          ref={fileRef} 
          accept="text/csv" 
          customUpload={true} 
          uploadHandler={onImportCSV} 
          mode="basic" 
          auto={true} 
          chooseLabel="Upload CSV" 
          style={{ display: 'none' }}
        />
        <Button 
          icon="pi pi-download" 
          className="p-button-help p-button-sm" 
          onClick={onExportCSV} 
          disabled={!selectedCustomers || selectedCustomers.length === 0}
          tooltip={selectedCustomers && selectedCustomers.length > 0 ? "Download selected customers as CSV" : "Select customers to export"}
          tooltipOptions={{ position: 'bottom', showOnDisabled: true }}
        />
        {isAdmin && onViewChangelog && (
          <Button 
            icon="pi pi-history" 
            className="p-button-secondary p-button-sm" 
            onClick={onViewChangelog} 
            tooltip="View Changes"
            tooltipOptions={{ position: 'bottom' }}
          />
        )}
        <Button 
          icon="pi pi-question-circle" 
          className="p-button-sm" 
          onClick={onHelp}
          tooltip="Help"
          tooltipOptions={{ position: 'bottom' }}
        />
      </div>
    </div>
  );

  const DataTableAny = DataTable as any;

  // Render "Select All" banner (Gmail-style)
  const renderSelectAllBanner = () => {
    const total = filteredCustomers.length;
    const selectedCount = selectedCustomers?.length || 0;
    const pageCount = pageRows > 0 ? Math.ceil(total / pageRows) : 1;

    const bannerStyle: React.CSSProperties = {
      backgroundColor: '#E3F2FD',
      border: '1px solid #90CAF9',
      borderRadius: '4px',
      padding: '0.75rem 1rem',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '0.875rem'
    };
    const linkStyle: React.CSSProperties = { color: '#1976D2', textDecoration: 'underline', cursor: 'pointer' };

    // State 1: every customer across all pages is selected — confirm the scope clearly
    // so the user knows a bulk action affects the whole list, not just this page.
    if (selectAllMode === 'all' && selectedCount >= total && total > 0) {
      return (
        <div style={bannerStyle}>
          <span style={{ color: '#1976D2', fontWeight: 500 }}>
            All <strong>{total}</strong> customer{total !== 1 ? 's' : ''} in the entire list are selected (every page, not just this one).
          </span>
          <a href="#" onClick={(e) => { e.preventDefault(); handleClearSelection(); }} style={linkStyle}>
            Clear selection
          </a>
        </div>
      );
    }

    // State 2: only the current page's rows are selected, and more pages exist —
    // offer to extend the selection to every page.
    if (!showSelectAllBanner) return null;
    return (
      <div style={bannerStyle}>
        <div>
          <span style={{ color: '#1976D2', fontWeight: 500 }}>
            Only the <strong>{selectedCount}</strong> customer{selectedCount !== 1 ? 's' : ''} on this page {selectedCount !== 1 ? 'are' : 'is'} selected.
          </span>
          {' '}
          <a href="#" onClick={(e) => { e.preventDefault(); handleSelectAll(); }} style={linkStyle}>
            Select all {total} customers across all {pageCount} pages
          </a>
        </div>
        <a href="#" onClick={(e) => { e.preventDefault(); handleClearSelection(); }} style={linkStyle}>
          Clear selection
        </a>
      </div>
    );
  };

  return (
    <>
      {renderSelectAllBanner()}
      <DataTableAny 
      key={
        inlineDropdown 
          ? `editing-${inlineDropdown.customer.customerId}-${inlineDropdown.field}` 
          : inlineCheckbox
            ? `editing-${inlineCheckbox.customer.customerId}-${inlineCheckbox.field}`
            : 'normal'
      }
      ref={dt} 
      value={filteredCustomers} 
      selection={selectedCustomers} 
      onSelectionChange={(e: any) => onSelectionChange(e.value || [])}
      selectionPageOnly
      selectionMode="checkbox"
      onRowClick={undefined}
      dataKey="customerId" 
      paginator 
      rows={pageRows} 
      rowsPerPageOptions={[5, 10, 25, 50]}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} customers"
      sortField="valid" 
      sortOrder={1} 
      size="small"
      rowClassName={rowClassName}
      globalFilter={globalFilter} 
      header={header} 
      csvSeparator=";"
      style={{ fontSize: '0.875rem' }}
      responsiveLayout="stack"
      breakpoint="960px"
      reorderableColumns={true}
      onColReorder={handleColumnReorder}
      first={pageFirst}
      onPage={(e: any) => { setPageFirst(e.first); setPageRows(e.rows); }}
    >
      <Column selectionMode="multiple" style={{ width: '3rem' }} exportable={false}></Column>
      <Column field="customerNr" header="Customer Nr." sortable style={{ width: '8.5rem' }} body={createEditableCellTemplate('customerNr')}></Column>
      <Column field="customerId" hidden={true} header="Customer Id" exportable={false} sortable></Column>
      <Column field="primaryEmail" header="Emails" body={editableEmailTemplate} sortable style={{ width: '5rem', overflow: 'hidden', textOverflow: 'ellipsis' }}></Column>
      <Column field="secondaryEmail" hidden={true} header="SecondaryEmail" exportable={true}></Column>
      <Column field="organization" hidden={!isAdmin} header={organizationHeader()} sortable style={{ width: '8rem' }}></Column>
      <Column field="company" header="Company" body={editableCompanyTemplate} sortable style={{ width: '6.5rem', overflow: 'hidden', textOverflow: 'ellipsis' }}></Column>
      <Column field="company2" hidden={true} header="Company2" exportable={true}></Column>
      <Column field="country" header={countryHeader()} body={editableCountryTemplate} sortable style={{ width: '5rem', minWidth: '5rem', maxWidth: '5rem', textAlign: 'center' }}></Column>
      <Column field="language" header="Lang" body={editableLanguageTemplate} sortable style={{ width: '4rem', minWidth: '4rem', maxWidth: '4rem', textAlign: 'center' }}></Column>
      <Column field="telephone" header="Phone" sortable body={editablePhoneTemplate} style={{ width: '4.5rem', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} bodyStyle={{ textAlign: 'left' }} headerClassName="header-align-left"></Column>
      <Column field="firstName" header="First Name" sortable style={{ width: '7rem', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} bodyStyle={{ textAlign: 'left' }} body={createEditableCellTemplate('firstName')} headerClassName="header-align-left"></Column>
      <Column field="surName" header="Last Name" sortable style={{ width: '7rem', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} bodyStyle={{ textAlign: 'left' }} body={createEditableCellTemplate('surName')} headerClassName="header-align-left"></Column>
      <Column 
        field="pkwData" 
        header={<span title="PKW Data">🚗</span>} 
        sortable 
        style={{ width: '3rem', textAlign: 'center' }} 
        headerStyle={{ textAlign: 'center' }}
        bodyStyle={{ textAlign: 'center' }}
        body={editableBooleanTemplate('pkwData')}
        headerClassName="header-align-center"
      ></Column>
      <Column 
        field="lkwData" 
        header={<span title="NKW Data">🚚</span>} 
        sortable 
        style={{ width: '3rem', textAlign: 'center' }} 
        headerStyle={{ textAlign: 'center' }}
        bodyStyle={{ textAlign: 'center' }}
        body={editableBooleanTemplate('lkwData')}
        headerClassName="header-align-center"
      ></Column>
      <Column 
        header="" 
        body={editButtonTemplate}
        exportable={false}
        style={{ width: '3rem', textAlign: 'center' }}
        headerStyle={{ textAlign: 'center' }}
        bodyStyle={{ textAlign: 'center' }}
      ></Column>
      <Column field="createdAt" header="CreatedAt" hidden exportable={false} sortable style={{ width: '1rem' }}></Column>
      <Column field="updatedAt" header="UpdatedAt" hidden exportable={false} sortable style={{ width: '1rem' }}></Column>
      <Column field="updatedBy" header="UpdatedBy" exportable={false} hidden sortable style={{ width: '1rem' }}></Column>
      <Column field="valid" header="Valid?" hidden exportable={false} body={validationBodyTemplate} sortable style={{ width: '1rem' }}></Column>
    </DataTableAny>
    {renderFilterDropdown()}
    {renderCountryFilterDropdown()}
    
    {/* Render cell editor overlay */}
    {editingCell && (
      <CellEditor
        customer={editingCell.customer}
        field={editingCell.field}
        value={editingCell.value}
        cellRect={editingCell.cellRect}
        onSave={handleCellSave}
        onCancel={handleCellCancel}
      />
    )}
    </>
  );
};
